import React from "react";
import PropTypes from "prop-types";
import Link from "gatsby-link";
import styled from "@emotion/styled";
import { graphql } from "gatsby";

// src imports for ramda are tiny compared to the whole package!
import compose from "ramda/src/compose";
import setPropTypes from "recompose/setPropTypes";

import Layout from "../layouts";

const Timestamp = styled.time`
  opacity: 0.6;
  font-size: smaller;
`;

const ArticleWrapper = styled.article`
  text-align: left;
`;

const ArticleBody = styled.div`
  div {
    text-align: justify;
    margin-top: 1rem;

    p:last-of-type {
      margin-bottom: 0;
    }

    code {
      background-color: var(--background-color-light);
    }
  }

  position: relative;
  overflow: hidden;
`;

const BlogHeading = styled.h3`
  margin-bottom: 0.25rem;
  a {
    background-image: none;
  }
`;

const Tag = styled.span`
  font-size: smaller;
  margin-right: 0.5rem;
  margin-bottom: 0.25rem;
  a,
  span {
    background-image: none;
    text-shadow: none;
    background-color: var(--background-color-light);
    padding: 0.25rem;
  }
`;

class Tags extends React.Component {
  static propTypes = {
    tags: PropTypes.arrayOf(String)
  };

  static defaultProps = {
    tags: []
  };

  render() {
    const { tags } = this.props;
    return (
      <div style={{ marginBottom: "0.25rem" }}>
        {tags.map(tag => (
          <Tag key={tag}>
            <span to={`/blog/tags/${tag}`}>{tag}</span>
          </Tag>
        ))}
      </div>
    );
  }
}

const ReadMoreFade = styled.section`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  font-size: smaller;
  margin: 0;
  padding: 4rem 0 0.25rem;
  a {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    opacity: 0.5;
    transition: opacity 0.25s ease-in-out;
    :hover {
      opacity: 1;
    }
    span {
      position: absolute;
      right: 0;
      bottom: 0;
    }
  }

  @media (max-width: 524px) {
    a {
      opacity: 1;
      span {
        left: 50%;
        top: 50%;
        transform: translate(-50%, 50%);
      }
    }
  }

  /* "transparent" only works here because == rgba(0,0,0,0) */
  background-image: linear-gradient(
    to bottom,
    transparent,
    var(--background-color) 70%
  );
  :hover {
    background-image: linear-gradient(
      to bottom,
      transparent,
      var(--background-color) 100%
    );
  }
`;

const ReadMore = ({ to }) => (
  <ReadMoreFade>
    <Link to={to}>
      <span>Read more...</span>
    </Link>
  </ReadMoreFade>
);

const enhance = compose(
  setPropTypes({
    data: PropTypes.shape({
      id: PropTypes.string,
      slug: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      written: PropTypes.string,
      body: PropTypes.string,
      tags: [PropTypes.string].isRequired
    }),
    slugPrefix: PropTypes.string,
    excerpt: PropTypes.bool
  })
);

const BlogPost = ({ data, excerpt = false, slugPrefix = "/blog/" }) => {
  let frontmatter;
  let __html;

  if (data.frontmatter) {
    frontmatter = data.frontmatter;
  } else {
    frontmatter = data.markdownRemark.frontmatter;
    __html = data.markdownRemark.html;
  }

  const post = (
    <ArticleWrapper key={frontmatter.slug}>
      <BlogHeading>
        <Link to={slugPrefix + frontmatter.slug}>{frontmatter.title}</Link>
      </BlogHeading>
      <Timestamp>{frontmatter.written || "Unknown"}</Timestamp>
      <Tags tags={frontmatter.tags} />
      {__html && (
        <ArticleBody>
          <div dangerouslySetInnerHTML={{ __html }} />
          {excerpt && <ReadMore to={slugPrefix + frontmatter.slug} />}
        </ArticleBody>
      )}
    </ArticleWrapper>
  );

  if (__html) {
    return <Layout title={`${frontmatter.title} - Angel Piscola`} back="/blog">{post}</Layout>;
  }
  return post;
};

export default enhance(BlogPost);

export const pageQuery = graphql`
  query($slug: String!) {
    markdownRemark(frontmatter: { slug: { eq: $slug } }) {
      html
      frontmatter {
        written(formatString: "MMMM DD, YYYY")
        slug
        title
        tags
      }
    }
  }
`;
